import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ArrowLeft, Plus, X, Upload, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const EditRaffle = () => {
  const { raffleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    unit_price: '',
    prizes: [''],
    prize_images: [],
    logo: null,
    payment_alias: '',
    whatsapp_number: '',
    payment_holder_name: '',
    observations: '',
  });

  useEffect(() => {
    fetchRaffle();
  }, [raffleId]);

  const fetchRaffle = async () => {
    try {
      const response = await axios.get(`${API}/raffles/${raffleId}`);
      const raffle = response.data;
      setFormData({
        title: raffle.title,
        unit_price: raffle.unit_price.toString(),
        prizes: raffle.prizes,
        prize_images: raffle.prize_images || [],
        logo: raffle.logo,
        payment_alias: raffle.payment_alias,
        whatsapp_number: raffle.whatsapp_number,
        payment_holder_name: raffle.payment_holder_name || '',
        observations: raffle.observations || '',
      });
      // Check if current user is owner
      try {
        const meResponse = await axios.get(`${API}/auth/me`);
        setIsOwner(meResponse.data.is_owner === true);
      } catch (e) {}
    } catch (error) {
      console.error('Error fetching raffle:', error);
      toast.error('Error al cargar rifa');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handlePrizeChange = (index, value) => {
    const newPrizes = [...formData.prizes];
    newPrizes[index] = value;
    setFormData({ ...formData, prizes: newPrizes });
  };

  const addPrize = () => {
    setFormData({ ...formData, prizes: [...formData.prizes, ''] });
  };

  const removePrize = (index) => {
    const newPrizes = formData.prizes.filter((_, i) => i !== index);
    setFormData({ ...formData, prizes: newPrizes });
  };

  const handleImageUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    const maxSize = 2 * 1024 * 1024;

    for (let file of files) {
      if (file.size > maxSize) {
        toast.error('Las imágenes deben ser menores a 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        if (type === 'logo') {
          setFormData({ ...formData, logo: base64 });
        } else {
          setFormData({
            ...formData,
            prize_images: [...formData.prize_images, base64]
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removePrizeImage = (index) => {
    const newImages = formData.prize_images.filter((_, i) => i !== index);
    setFormData({ ...formData, prize_images: newImages });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const validPrizes = formData.prizes.filter(p => p.trim() !== '');
      if (validPrizes.length === 0) {
        toast.error('Agregá al menos un premio');
        setSaving(false);
        return;
      }

      const payload = {
        title: formData.title,
        unit_price: parseFloat(formData.unit_price),
        prizes: validPrizes,
        prize_images: formData.prize_images,
        logo: formData.logo,
        payment_alias: formData.payment_alias,
        whatsapp_number: formData.whatsapp_number,
        payment_holder_name: formData.payment_holder_name || undefined,
        observations: formData.observations,
      };

      await axios.put(`${API}/raffles/${raffleId}`, payload);
      toast.success('Rifa actualizada exitosamente');
      navigate('/admin');
    } catch (error) {
      console.error('Error updating raffle:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar rifa');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Cargando...</div>;
  }

  return (
    <div className="space-y-6">
      <Button variant="outline" onClick={() => navigate('/admin')} data-testid="back-button">
        <ArrowLeft className="mr-2" size={20} />
        Volver
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="text-3xl" data-testid="edit-raffle-title">Editar Rifa</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Título de la Rifa *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                  data-testid="raffle-title"
                />
              </div>

              <div>
                <Label htmlFor="unit_price">Valor número/fila *</Label>
                <Input
                  id="unit_price"
                  type="text"
                  inputMode="decimal"
                  value={formData.unit_price}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                    handleInputChange('unit_price', v);
                  }}
                  required
                  data-testid="unit-price"
                />
              </div>

              <div>
                <Label htmlFor="payment_alias">Alias de Pago *</Label>
                <Input
                  id="payment_alias"
                  value={formData.payment_alias}
                  onChange={(e) => handleInputChange('payment_alias', e.target.value)}
                  required
                  data-testid="payment-alias"
                />
              </div>

              <div>
                <Label htmlFor="payment_holder_name">A nombre de</Label>
                <Input
                  id="payment_holder_name"
                  value={formData.payment_holder_name}
                  onChange={(e) => handleInputChange('payment_holder_name', e.target.value)}
                  placeholder="Ej: Juan Perez"
                  data-testid="payment-holder-name"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Nombre del titular de la cuenta del alias
                </p>
              </div>

              <div>
                <Label htmlFor="whatsapp_number">Número de WhatsApp *</Label>
                <Input
                  id="whatsapp_number"
                  value={formData.whatsapp_number}
                  onChange={(e) => handleInputChange('whatsapp_number', e.target.value)}
                  required
                  data-testid="whatsapp-number"
                />
              </div>

              <div>
                <Label htmlFor="observations">Observaciones</Label>
                <textarea
                  id="observations"
                  value={formData.observations}
                  onChange={(e) => handleInputChange('observations', e.target.value)}
                  placeholder="Aclaraciones para los participantes (fecha, condiciones, etc.)"
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="observations-input"
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Premios</h3>
              {formData.prizes.map((prize, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={prize}
                    onChange={(e) => handlePrizeChange(index, e.target.value)}
                    placeholder={`Premio ${index + 1}`}
                    data-testid={`prize-${index}`}
                  />
                  {formData.prizes.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removePrize(index)}
                      data-testid={`remove-prize-${index}`}
                    >
                      <X size={18} />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={addPrize}
                className="w-full"
                data-testid="add-prize"
              >
                <Plus className="mr-2" size={18} />
                Agregar Premio
              </Button>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Imágenes</h3>
              <div>
                <Label>Logo</Label>
                <div className="mt-2">
                  {formData.logo ? (
                    <div className="relative inline-block">
                      <img src={formData.logo} alt="Logo" className="w-32 h-32 object-contain border rounded" />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => setFormData({ ...formData, logo: null })}
                        className="absolute top-0 right-0"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-500 transition-colors">
                        <Upload className="mx-auto mb-2" size={32} />
                        <p className="text-sm text-gray-600">Click para subir logo</p>
                      </div>
                      <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'logo')} className="hidden" />
                    </label>
                  )}
                </div>
              </div>

              <div>
                <Label>Imágenes de Premios</Label>
                <div className="mt-2 space-y-4">
                  {formData.prize_images.length > 0 && (
                    <div className="grid grid-cols-4 gap-4">
                      {formData.prize_images.map((img, index) => (
                        <div key={index} className="relative">
                          <img src={img} alt={`Premio ${index + 1}`} className="w-full h-24 object-cover border rounded" />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => removePrizeImage(index)}
                            className="absolute top-0 right-0"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <label className="cursor-pointer">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-500 transition-colors">
                      <Upload className="mx-auto mb-2" size={32} />
                      <p className="text-sm text-gray-600">Click para agregar imágenes</p>
                    </div>
                    <input type="file" accept="image/*" multiple onChange={(e) => handleImageUpload(e, 'prizes')} className="hidden" />
                  </label>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-6">
              <Button type="button" variant="outline" onClick={() => navigate('/admin')} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700" disabled={saving} data-testid="submit-edit">
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditRaffle;
